import { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Container,
    Box,
    useScrollTrigger,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import {
    useNavbarAuth,
    useNavbarNavigation,
    NavLinks,
    UserMenu,
    MobileDrawer,
} from './navbar';
import {
    resolveNavbarBgColor,
    resolveNavbarBoxShadow,
    resolveLogoColor,
    resolveLogoHoverColor,
    resolveMenuButtonColor
} from './navbar/navbarThemeHelpers';

function isHeroPagePath(pathname) {
    return pathname === '/' ||
        pathname.startsWith('/camps') ||
        pathname.startsWith('/programs') ||
        pathname.startsWith('/races');
}

function Navbar() {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const isDark = theme.palette.mode === 'dark';

    const { user, isAdmin, isProgramsManager, isCommunityMember, registeredProgramIds } = useNavbarAuth();
    const { filteredNavItems, isItemRegistered } = useNavbarNavigation({
        user,
        isAdmin,
        isProgramsManager,
        isCommunityMember,
        registeredProgramIds,
    });

    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 100,
    });

    const showTransparent = isHeroPagePath(location.pathname) && !trigger;

    const handleDrawerToggle = () => {
        setMobileOpen((prev) => !prev);
    };

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    bgcolor: resolveNavbarBgColor(showTransparent, isDark),
                    borderBottom: showTransparent ? 'none' : `1px solid ${theme.palette.divider}`,
                    backdropFilter: 'blur(8px)',
                    boxShadow: resolveNavbarBoxShadow(showTransparent, isDark),
                    transition: 'all 0.3s ease',
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar
                        sx={{
                            justifyContent: 'space-between',
                            px: { xs: 0, sm: 2 },
                            minHeight: '48px !important',
                            '@media (min-width: 600px)': {
                                minHeight: '52px !important',
                            },
                        }}
                    >
                        {/* Logo */}
                        <Typography
                            component={Link}
                            to="/"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '1.05rem', sm: '1.15rem' },
                                color: resolveLogoColor(showTransparent, isDark),
                                textDecoration: 'none',
                                letterSpacing: '0.05em',
                                transition: 'color 0.3s ease',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                '&:hover': {
                                    color: resolveLogoHoverColor(showTransparent, isDark),
                                },
                            }}
                        >
                            TAS SKI TEAM
                        </Typography>

                        {/* Desktop Navigation */}
                        {!isMobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <NavLinks
                                    items={filteredNavItems}
                                    showTransparent={showTransparent}
                                    isItemRegistered={isItemRegistered}
                                />
                                <UserMenu
                                    user={user}
                                    showTransparent={showTransparent}
                                />
                            </Box>
                        )}

                        {/* Mobile menu button */}
                        {isMobile && (
                            <IconButton
                                edge="end"
                                aria-label="open navigation menu"
                                aria-expanded={mobileOpen}
                                onClick={handleDrawerToggle}
                                sx={{
                                    color: resolveMenuButtonColor(showTransparent, isDark),
                                }}
                            >
                                <MenuIcon fontSize="large" />
                            </IconButton>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile drawer */}
            <MobileDrawer
                open={mobileOpen}
                onClose={handleDrawerToggle}
                items={filteredNavItems}
                user={user}
                isItemRegistered={isItemRegistered}
            />
        </>
    );
}

export default Navbar;
